import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, ref, watch } from 'vue';

import { generalApi } from '@/lib/api/general';
import { useCompanyFeatureComposable } from '@/composables/useCompanyFeatures';
import { defineMessages } from '@/lib/i18n';
import sharedMessages from '@/lib/shared-messages';
import { i18n } from '@/plugins/vue-i18n';
import { formatToDate } from '@/utils/datetime';
import type {
  SupplierInquiryResponse,
  SupplierInquiryStatus,
} from '@/interfaces/api/general/supplierInquiry';

type NotificationColor = 'success' | 'error' | 'warning' | 'info';

interface Props {
  sdsUuid: string;
  /** All サプライヤー問い合わせ requests for this SDS, newest first
   *  (parent fetches sorted by requested_at DESC). Since PdM 2026-07-08
   *  re-request is allowed once no request is in NOT_OBTAINED/CONTACTED,
   *  so an SDS can now accumulate multiple rows — each one gets its own
   *  status card in the drawer under a single 一覧を見る header. */
  existingRequests?: SupplierInquiryResponse[];
  /** Supplier prefill values read from sds.section1 (V1 Figma:
   *  「SDSから取得した内容が予め入力されていますが、必要に応じて変更してください。」). */
  defaultSupplierCompanyName?: string | null;
  defaultSupplierContactEmail?: string | null;
  defaultSupplierContactPhone?: string | null;
  disabled?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SupplierInquiryRequestButton',
  props: {
    sdsUuid: null,
    existingRequests: { default: () => [] },
    defaultSupplierCompanyName: { default: null },
    defaultSupplierContactEmail: { default: null },
    defaultSupplierContactPhone: { default: null },
    disabled: { type: Boolean, default: false }
  },
  emits: ["requested"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'requested'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { sdsUuid: string, existingRequests: SupplierInquiryResponse[], defaultSupplierCompanyName: string | null, defaultSupplierContactEmail: string | null, defaultSupplierContactPhone: string | null, disabled: boolean };

const $t = i18n.tc.bind(i18n);



// A new request is blocked while any existing row is still in a state
// that the DB partial unique index `uq_si_active_request` covers. Keep
// this mirror in sync with backend BLOCKING_STATUSES.
const BLOCKING_STATUSES: readonly SupplierInquiryStatus[] = [
  'NOT_OBTAINED',
  'CONTACTED',
];
const hasBlockingRequest = computed(() =>
  props.existingRequests.some((r) => BLOCKING_STATUSES.includes(r.status))
);
const canRequest = computed(() => !hasBlockingRequest.value);

// Format ISO datetime for the 依頼日 label in the request card. Uses the
// locale-aware `formatToDate` (JST-converted) so English users see e.g.
// "Jul 3, 2026" and Japanese users see "2026年07月03日".
const formatRequestedAt = (iso?: string): string =>
  iso ? formatToDate(iso) : '';



const m = defineMessages({
  // Action button on the SDS detail page (under サプライヤーへの確認日)

  // Dialog (V1 Figma node 36684:390334)

  dialogDesc:
    'ケミカンがサプライヤーへ連絡し、問い合わせ内容の対応をいたします。\nサプライヤー情報と問い合わせ内容を入力してご依頼ください。',
  requiredNote: '※メールアドレス、または電話番号のいずれか一方は必須項目です',
  errorRequired: '必須項目です',
  errorInvalidEmail: 'メールアドレスの形式が正しくありません',
  errorInvalidPhone: '電話番号の形式が正しくありません',
  errorEmailOrPhone: 'メールアドレスまたは電話番号のいずれかを入力してください',
  sectionTitle: 'サプライヤー情報',
  companyLabel: '企業名',
  // noteLabel / customerNoteCardLabel / chip labels all use sharedMessages
  // (see chipTextFor and template refs below) — the CI i18n duplicate check
  // errors if the same string is defined in multiple defineMessages blocks.
  notePlaceholder: 'サプライヤーへの問い合わせ内容を入力してください。',

  // Section header link (label "サプライヤー問い合わせ" comes from sharedMessages.supplierInquiry)
  listLink: '一覧を見る',
  // Inline status row
  statusRowLabel: 'ケミカン対応状況：',
  requestDateLabel: '依頼日：',
  supplierCommentLabel: 'サプライヤーからのコメント',
  // Feedback
  successMsg: 'サプライヤー問い合わせを依頼しました',
  errorMsg: '依頼に失敗しました',
});

// Local snackbar (matches the SdsGhsLabelPrintDialog.vue pattern used
// elsewhere in this codebase — there's no global notification store).
const notificationState = reactive<{
  visible: boolean;
  message: string;
  color: NotificationColor;
}>({
  visible: false,
  message: '',
  color: 'success',
});

const showNotification = (message: string, color: NotificationColor) => {
  notificationState.visible = false;
  notificationState.message = message;
  notificationState.color = color;
  requestAnimationFrame(() => {
    notificationState.visible = true;
  });
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Phone format: only digits, +, -, (), spaces, dots. Accepts common JP
// notations (03-1234-5678, 090-1234-5678, +81 3-1234-5678, etc.) and
// international variants. Requires at least 8 digits so a stray word
// or a single number cannot slip through.
// Multiple numbers may be entered comma-separated (SDS section1 often
// stores 2+ contact lines pre-joined this way); each segment is
// validated independently.
const PHONE_ALLOWED_RE = /^[+\d\-()\s.]+$/;
const PHONE_MIN_DIGITS = 8;
const PHONE_MAX_DIGITS = 15;

const dialogOpen = ref(false);
const supplierCompanyName = ref<string>('');
const supplierContactEmail = ref<string>('');
const supplierContactPhone = ref<string>('');
const customerNote = ref<string>('');
const submitting = ref(false);
// Suppress error messages until the user has interacted (first blur) so the
// form doesn't yell at you the moment the dialog opens.
const showErrors = ref(false);

// Hydrate the dialog on each open (and whenever the SDS or prefills change
// while open). Reset note since it's request-specific.
watch(
  () =>
    [
      dialogOpen.value,
      props.defaultSupplierCompanyName,
      props.defaultSupplierContactEmail,
      props.defaultSupplierContactPhone,
      props.sdsUuid,
    ] as const,
  ([open]) => {
    if (open) {
      supplierCompanyName.value = props.defaultSupplierCompanyName ?? '';
      const rawEmail = props.defaultSupplierContactEmail ?? '';
      supplierContactEmail.value = EMAIL_RE.test(rawEmail.trim())
        ? rawEmail
        : '';
      supplierContactPhone.value = (
        props.defaultSupplierContactPhone ?? ''
      ).replaceAll('|', ', ');
      customerNote.value = '';
      showErrors.value = false;
    }
  },
  { immediate: true }
);

// SUPPLIER_INQUIRY is a paid feature — when the company hasn't purchased
// it the request CTA must be disabled with an "add-on" tooltip, matching
// the pattern used elsewhere in the app for gated features.
const { isFeatureActive, COMPANY_FEATURES, generateInactiveMessage } =
  useCompanyFeatureComposable();
const isSupplierInquiryActive = computed(() =>
  isFeatureActive(COMPANY_FEATURES.SUPPLIER_INQUIRY)
);
const inactiveTooltip = computed(() =>
  generateInactiveMessage($t(sharedMessages.supplierInquiry))
);
const requestBtnDisabled = computed(
  () => props.disabled || !isSupplierInquiryActive.value
);

// Per-request chip label. Keyed off the raw status.
const chipTextFor = (r: SupplierInquiryResponse): string => {
  switch (r.status) {
    case 'NOT_OBTAINED':
      return $t(sharedMessages.siStatusNotObtained);
    case 'CONTACTED':
      return $t(sharedMessages.siStatusContacted);
    case 'OBTAINED':
      return $t(sharedMessages.siStatusObtained);
    case 'NOT_APPLICABLE':
      return $t(sharedMessages.siStatusNotApplicable);
    case 'CANCELLED':
      return $t(sharedMessages.siStatusCancelled);
    default:
      return '';
  }
};
const chipStyleFor = (
  r: SupplierInquiryResponse
): { backgroundColor: string; color: string } => {
  switch (r.status) {
    case 'NOT_OBTAINED':
      return { backgroundColor: '#01B7D6', color: '#ffffff' };
    case 'CONTACTED':
      return { backgroundColor: '#5D87FF', color: '#ffffff' };
    case 'OBTAINED':
      return { backgroundColor: '#2CAC9F', color: '#ffffff' };
    case 'NOT_APPLICABLE':
      return { backgroundColor: '#FF5454', color: '#ffffff' };
    case 'CANCELLED':
      return { backgroundColor: '#949497', color: '#ffffff' };
    default:
      return { backgroundColor: '', color: '' };
  }
};
// Which requests get the サプライヤーからのコメント panel:
//   OBTAINED (対応完了 — customer-facing terminal after PdM 2026-07-17):
//     shows customer_message.
//   NOT_APPLICABLE: shows not_applicable_reason.
//   Everything else: no comment panel.
const supplierCommentFor = (r: SupplierInquiryResponse): string | null => {
  if (r.status === 'OBTAINED') {
    return r.customer_message || null;
  }
  if (r.status === 'NOT_APPLICABLE') {
    return r.not_applicable_reason || null;
  }
  return null;
};

// LOCKED VALIDATION RULES (confirmed with user; do not renegotiate):
// - 企業名: REQUIRED (always — asterisk shown on label)
// - メール OR 電話: at least one must be filled (per Figma red warning text)
// - メール: when filled, must match EMAIL_RE
// - 問い合わせ内容: REQUIRED (per PdM update — non-SDS inquiries also
//   route through this dialog, so an empty body is not meaningful)

const companyError = computed(() =>
  !supplierCompanyName.value.trim() ? $t(m.errorRequired) : ''
);
// Email shows: invalid-format if filled but malformed, or
// "either email or phone required" if both this field and phone are empty.
// Phone shows the same either-required message when both are empty.
const eitherError = computed(() => {
  const hasEmail = supplierContactEmail.value.trim() !== '';
  const hasPhone = supplierContactPhone.value.trim() !== '';
  return !hasEmail && !hasPhone ? $t(m.errorEmailOrPhone) : '';
});
const emailError = computed(() => {
  const v = supplierContactEmail.value.trim();
  if (!v) return eitherError.value;
  if (!EMAIL_RE.test(v)) return $t(m.errorInvalidEmail);
  return '';
});
const phoneError = computed(() => {
  const v = supplierContactPhone.value.trim();
  if (!v) return eitherError.value;
  // Split on comma/、/;/newline so pre-filled multi-number strings from
  // SDS section 1 (e.g. "03-1234-5678, 03-9876-5432") validate segment-
  // by-segment. Empty segments (trailing comma, double comma) are ignored.
  const segments = v
    .split(/[,、;；\n]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  if (segments.length === 0) return $t(m.errorInvalidPhone);
  for (const seg of segments) {
    if (!PHONE_ALLOWED_RE.test(seg)) return $t(m.errorInvalidPhone);
    const digitCount = seg.replace(/\D/g, '').length;
    if (digitCount < PHONE_MIN_DIGITS || digitCount > PHONE_MAX_DIGITS) {
      return $t(m.errorInvalidPhone);
    }
  }
  return '';
});
const customerNoteError = computed(() =>
  !customerNote.value.trim() ? $t(m.errorRequired) : ''
);

const isValid = computed(
  () =>
    !companyError.value &&
    !emailError.value &&
    !phoneError.value &&
    !eitherError.value &&
    !customerNoteError.value
);

const submit = async () => {
  if (!isValid.value) {
    showErrors.value = true;
    if (eitherError.value) {
      showNotification(eitherError.value, 'warning');
    }
    return;
  }
  submitting.value = true;
  try {
    await generalApi.createSupplierInquiryRequest({
      sds_id: props.sdsUuid,
      supplier_company_name: supplierCompanyName.value.trim() || null,
      supplier_contact_email: supplierContactEmail.value.trim() || null,
      supplier_contact_phone: supplierContactPhone.value.trim() || null,
      customer_note: customerNote.value.trim() || null,
    });
    showNotification($t(m.successMsg), 'success');
    dialogOpen.value = false;
    emit('requested');
  } catch (e: unknown) {
    // Backend raises 4xx with `detail` for already_active / not_permitted /
    // not_found. Surface the detail directly so the user sees what's wrong.
    const err = e as {
      response?: { data?: { detail?: string }; status?: number };
    };
    const detail = err?.response?.data?.detail;
    const status = err?.response?.status;
    showNotification(detail || $t(m.errorMsg), 'error');
    // 409 = already_active → the row exists on the server; refresh parent
    // so the drawer flips into the "request exists" state.
    if (status === 409) {
      dialogOpen.value = false;
      emit('requested');
    }
  } finally {
    submitting.value = false;
  }
};

return { __sfc: true,$t, props, BLOCKING_STATUSES, hasBlockingRequest, canRequest, formatRequestedAt, emit, m, notificationState, showNotification, EMAIL_RE, PHONE_ALLOWED_RE, PHONE_MIN_DIGITS, PHONE_MAX_DIGITS, dialogOpen, supplierCompanyName, supplierContactEmail, supplierContactPhone, customerNote, submitting, showErrors, isFeatureActive, COMPANY_FEATURES, generateInactiveMessage, isSupplierInquiryActive, inactiveTooltip, requestBtnDisabled, chipTextFor, chipStyleFor, supplierCommentFor, companyError, eitherError, emailError, phoneError, customerNoteError, isValid, submit, sharedMessages }
}

})